/**
 * Service content registry.
 *
 * Routes and the sitemap are generated from THIS registry, not from the full
 * `SERVICE_SLUGS` union in ../types.ts. The distinction matters: `SERVICE_SLUGS`
 * is the set of services the site intends to cover, while this registry is the
 * set for which real content has actually been authored.
 *
 * Deriving routes from intent rather than from content is how sites end up
 * prerendering and indexing empty pages — the "thin page created to target a
 * keyword" failure mode. If a service has no content module here, it gets no
 * route, no sitemap entry, and no internal links, so it cannot be indexed
 * empty. Adding the content file is the single step that publishes it.
 *
 * `MISSING_SERVICE_CONTENT` below is therefore a live to-do list, and the
 * services index page uses it to avoid linking to pages that do not exist.
 */

import type { ServiceContent, ServiceSlug } from "@/content/types";
import { SERVICE_SLUGS } from "@/content/types";

import cosmeticDentistry from "./cosmetic-dentistry";
import dentalImplants from "./dental-implants";
import hollywoodSmile from "./hollywood-smile";
import oralSurgery from "./oral-surgery";
import orthodontics from "./orthodontics";
import pediatricDentistry from "./pediatric-dentistry";
import rootCanalTreatment from "./root-canal-treatment";
import veneers from "./veneers";

/**
 * Every service with authored content, keyed by slug.
 *
 * Partial by design — see the module comment. Keys must match the file's own
 * `slug` field; the assertion below enforces that.
 */
export const SERVICES: Partial<Record<ServiceSlug, ServiceContent>> = {
  "dental-implants": dentalImplants,
  "cosmetic-dentistry": cosmeticDentistry,
  veneers: veneers,
  "hollywood-smile": hollywoodSmile,
  orthodontics: orthodontics,
  "root-canal-treatment": rootCanalTreatment,
  "pediatric-dentistry": pediatricDentistry,
  "oral-surgery": oralSurgery,
};

/**
 * Guard against a registry key disagreeing with the content's own slug — which
 * would produce a page served at one URL while its metadata, canonical and
 * hreflang all claim another.
 */
for (const [key, service] of Object.entries(SERVICES)) {
  if (service && service.slug !== key) {
    throw new Error(
      `Service registry key "${key}" does not match its content slug "${service.slug}" ` +
        `(src/content/services/index.ts). These must be identical or the page's ` +
        `URL and its canonical/hreflang metadata will disagree.`
    );
  }
}

/** Slugs with real content — the authoritative list for routing and sitemaps. */
export const PUBLISHED_SERVICE_SLUGS: readonly ServiceSlug[] = SERVICE_SLUGS.filter(
  (slug) => SERVICES[slug] !== undefined
);

/**
 * Intended services still awaiting content. Not routed, not linked, not
 * indexed. Tracked in docs/content-roadmap.md.
 */
export const MISSING_SERVICE_CONTENT: readonly ServiceSlug[] = SERVICE_SLUGS.filter(
  (slug) => SERVICES[slug] === undefined
);

export function getService(slug: string): ServiceContent | undefined {
  return SERVICES[slug as ServiceSlug];
}
